import React, { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, IconButton, Paper, Typography, Divider } from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';

import ConversationList from './ConversationList';
import Searchbar from './Searchbar';
import AdminMessagingAPIs from '../../utilities/api/IMS/AdminMessagingAPIs';
import { setSelectedConversation } from '../../redux/slices/conversationSlice';

const ChatBar = () => {
  // Redux selectors
  const { user } = useSelector(state => state.user.user);
//   const conversations = useSelector(state => state.conversation?.conversations) || [];

  const { conversations } = useSelector(state => state.conversation);
  const dispatch = useDispatch();

  // React state for open/closed chat bar and user suggestions for search
  const [isOpen, setIsOpen] = useState(false);
  const [userSuggestions, setUserSuggestions] = useState([]);
  const [rightOffset, setRightOffset] = useState(20);

  // Hooks for navigation and location
  const navigate = useNavigate();
  const location = useLocation();

  // Refs to handle dragging logic
  const chatBarRef = useRef(null);
  const isDragging = useRef(false);
  const dragStarted = useRef(false);

  /**
   * Fetch thread details by id (optional utility if needed)
   */
  const fetchThreadDetail = async (id) => {
    try {
      const threadDetail = await AdminMessagingAPIs.GetThreadDetail(id);
      return {
        ...threadDetail,
        messages: threadDetail.messages.map(message => ({
          id: message.id,
          text: message.content,
          date: message.timestamp,
          sender: message.sender,
        })),
        otherUser: threadDetail.messages[0].sender === user.username
          ? threadDetail.messages[0].receivers.find(receiver => receiver !== user.username)
          : threadDetail.messages[0].sender
      };
    } catch (error) {
      console.error(`Error fetching thread detail for ID ${id}:`, error);
      return null;
    }
  };

  /**
   * Toggle open/close chat bar
   * Prevent toggle if a drag was just started to avoid accidental toggles
   */
  const toggleChatBar = () => {
    if (!dragStarted.current) {
      setIsOpen(!isOpen);
    }
    dragStarted.current = false;
  };

  /**
   * Handle search input change to fetch matching user/thread suggestions
   */
  const handleInputChange = async (event, newInputValue) => {
    if (newInputValue.length > 0) {
      const users = await AdminMessagingAPIs.SearchThreads(newInputValue);
      setUserSuggestions(users.map(user => ({
        id: user.thread_id,
        username: user.name,
      })));
    } else {
      setUserSuggestions([]);
    }
  };

  /**
   * Handle selection of user suggestion from search results
   */
  const handleUserSelect = (event, value) => {
    if (value) {
      dispatch(setSelectedConversation(value.id));
      navigate('/messages', { state: { from: location.pathname } });
    }
  };

  /**
   * Open full screen messages page
   */
  const handleFullScreenClick = (e) => {
    e.stopPropagation();
    navigate('/messages', { state: { from: location.pathname } });
  };

  /**
   * Handle clicking a conversation card to open that conversation
   */
  const handleChatCardClick = (id) => {
    dispatch(setSelectedConversation(id));
    navigate('/messages', { state: { from: location.pathname } });
  };

  /**
   * Start dragging the minimized chat bar (only when closed)
   */
  const startDrag = (e) => {
    if (!isOpen) {
      e.preventDefault();
      isDragging.current = true;
      dragStarted.current = false;
      document.body.style.userSelect = 'none'; // Prevent text selection while dragging
    }
  };

  /**
   * Stop dragging action
   */
  const stopDrag = () => {
    if (isDragging.current) {
      isDragging.current = false;
      dragStarted.current = true; // Flag that drag just ended to prevent toggle on mouseup
    }
    document.body.style.userSelect = '';
  };

  /**
   * Handle dragging movement, adjusting chat bar position on the screen
   */
  const onDrag = (e) => {
    if (isDragging.current && chatBarRef.current) {
      const newRightOffset = window.innerWidth - e.clientX - chatBarRef.current.offsetWidth / 2;
      const maxRightOffset = window.innerWidth - chatBarRef.current.offsetWidth;
      const minRightOffset = 0;

      if (newRightOffset >= minRightOffset && newRightOffset <= maxRightOffset) {
        setRightOffset(newRightOffset);
      }
    }
  };

  // Setup event listeners for dragging on mount and cleanup on unmount
  useEffect(() => {
    window.addEventListener('mousemove', onDrag);
    window.addEventListener('mouseup', stopDrag);
    return () => {
      window.removeEventListener('mousemove', onDrag);
      window.removeEventListener('mouseup', stopDrag);
    };
  }, []);

  // Hide ChatBar on specific routes
  const notAllowedPaths = [
    '/messages', '/login', '/register-instructions', '/profile-picture',
    '/position-enter', '/student-detail-1', '/more-detail',
    '/check-status', '/studentlogin', '/reset_password', '/forgot_password',
  ];
  if (notAllowedPaths.includes(location.pathname)) {
    return null;
  }

  return (
    <Box
      ref={chatBarRef}
      sx={{
        position: 'fixed',
        bottom: 0,
        right: rightOffset,
        width: '350px',
        height: isOpen ? '450px' : '50px',
        backgroundColor: '#F9FAFE',
        color: '#000',
        transition: 'all 0.3s',
        overflow: 'hidden',
        borderRadius: '10px 10px 0 0',
        boxShadow: '0px 4px 16px 0px #0000001A',
        border: '1px solid #E79A2B',
        cursor: !isOpen ? 'move' : 'default',
        zIndex: 1000,
      }}
      onMouseDown={!isOpen ? startDrag : null}
    >
      {/* Header Bar */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '10px',
          cursor: 'move',
        }}
        onMouseDown={startDrag}
        onClick={toggleChatBar}
      >
        <Typography variant="h6">Messages</Typography>

        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton color="inherit" onClick={handleFullScreenClick}>
            {/* Inline SVG for fullscreen icon */}
            <svg
              width="25"
              height="24"
              viewBox="0 0 25 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M13.4207 7.93359L16.1628 8.03367C16.5703 8.04855 16.8931 8.38325 16.8931 8.79108V11.406M11.3931 13.4336L16.4368 8.41516"
                stroke="#141B34"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M2.854 17C2.854 15.1144 2.854 14.1716 3.43979 13.5858C4.02557 13 4.96838 13 6.854 13H7.854C9.73962 13 10.6824 13 11.2682 13.5858C11.854 14.1716 11.854 15.1144 11.854 17V18C11.854 19.8856 11.854 20.8284 11.2682 21.4142C10.6824 22 9.73962 22 7.854 22H6.854C4.96838 22 4.02557 22 3.43979 21.4142C2.854 20.8284 2.854 19.8856 2.854 18V17Z"
                stroke="#141B34"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M2.854 8.5V10.5M14.854 2H10.854M22.854 14V10M14.354 22H16.354M2.91386 5.5C3.06787 4.43442 3.36747 3.67903 3.95025 3.09625C4.53303 2.51347 5.28842 2.21387 6.354 2.05986M19.354 2.05986C20.4196 2.21387 21.175 2.51347 21.7577 3.09625C22.3405 3.67903 22.6401 4.43442 22.7941 5.5M22.7941 18.5C22.6401 19.5656 22.3405 20.321 21.7577 20.9037C21.175 21.4865 20.4196 21.7861 19.354 21.9401"
                stroke="#141B34"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </IconButton>

          <IconButton color="inherit" onClick={toggleChatBar}>
            {isOpen ? <ExpandMore /> : <ExpandLess />}
          </IconButton>
        </Box>
      </Box>

      {/* Divider below header */}
      <Divider variant="middle" sx={{ backgroundColor: '#A6A6A6' }} />

      {/* Search bar form */}
      <Paper
        component="form"
        sx={{
          display: 'flex',
          alignItems: 'center',
          padding: '10px',
          boxShadow: 'none',
          backgroundColor: 'transparent',
          zIndex: 11000,
        }}
      >
        <Box sx={{ flex: 1, position: 'relative' }}>
          <Searchbar
            userSuggestions={userSuggestions}
            handleInputChange={handleInputChange}
            handleUserSelect={handleUserSelect}
          />
        </Box>
      </Paper>

      {/* Conversation list shown only when open */}
      {isOpen && (
        <Box
          sx={{
            padding: '0 10px 10px 10px',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            overflowY: 'auto',
            // Scrollbar styling for better UX
            '&::-webkit-scrollbar': {
              width: '3px',
              backgroundColor: 'transparent',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0,0,0,0.2)',
              borderRadius: '3px',
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.4)',
              },
            },
            scrollBehavior: 'smooth',
            '-webkit-overflow-scrolling': 'touch',
          }}
        >
          {conversations && conversations.length > 0 && conversations.map((conv) => (
            <Box key={conv.id} sx={{ borderBottom: '1px solid #A6A6A6' }}>
              <ConversationList
                userName={conv.userName}
                message={conv.message}
                date={new Date(conv.date).toLocaleString()}
                avatar="path_to_avatar"
                onClick={() => handleChatCardClick(conv.id)}
              />
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default ChatBar;